<script setup lang="ts">
import type { Payment } from "@/model/order/payment"
import { type PaymentKind, paymentKind } from "@/pages/Order/Payment/paymentKind"
import { defineTranslations } from "@/utils/defineTranslations"
import { $currency, $datetime } from "@/vf"

defineProps<{
    payments: Payment[]
    viewMode: "customer" | "admin"
}>()

const trans = defineTranslations({
    "paid": {
        de: "Zahlung erfolgt",
        en: "Payment completed",
    },
    "refund": {
        de: "Rückerstattung erfolgt",
        en: "Refund completed",
    },
    "refund_failed": {
        de: "Rückerstattung fehlgeschlagen",
        en: "Refund failed",
    },
    "refund_canceled": {
        de: "Rückerstattung abgebrochen",
        en: "Refund canceled",
    },
    "refund_pending": {
        de: "Rückerstattung wird verarbeitet",
        en: "Refund is being processed",
    },
    "reversal": {
        de: "Storno der Rückerstattung",
        en: "Refund reversal",
    },
    "prefix_date": {
        de: "Zahlung vom",
        en: "Payment from",
    },
    "booked_by": {
        de: "Gebucht von {name}",
        en: "Booked by {name}",
    },
    "method.creditcard": {
        de: "Kreditkarte",
        en: "Credit Card",
    },
    "method.paybybank": {
        de: "Sofortüberweisung",
        en: "Pay by Bank",
    },
    "method.banktransfer": {
        de: "Banküberweisung",
        en: "Bank Transfer",
    },
    "method.manual": {
        de: "Manuell gebucht",
        en: "Manually booked",
    },
})

const labels: Record<PaymentKind, string> = {
    reversal: trans("reversal"),
    failedRefund: trans("refund_failed"),
    canceledRefund: trans("refund_canceled"),
    pendingRefund: trans("refund_pending"),
    refund: trans("refund"),
    paid: trans("paid"),
}

const iconClasses: Record<PaymentKind, string> = {
    reversal: "fa-rotate-left text-muted",
    failedRefund: "fa-triangle-exclamation text-danger",
    canceledRefund: "fa-ban text-muted",
    pendingRefund: "fa-clock text-muted",
    refund: "fa-check text-success",
    paid: "fa-check text-success",
}

const textClasses: Record<PaymentKind, string> = {
    reversal: "text-muted",
    failedRefund: "text-danger",
    canceledRefund: "text-muted",
    pendingRefund: "text-muted",
    refund: "text-success",
    paid: "text-success",
}

const badgeClasses: Record<PaymentKind, string> = {
    reversal: "badge-secondary",
    failedRefund: "badge-danger",
    canceledRefund: "badge-secondary",
    pendingRefund: "badge-secondary",
    refund: "badge-success",
    paid: "badge-success",
}

function translatePaymentMethod(method: string) {
    switch (method) {
        case "creditcard":
            return trans("method.creditcard")
        case "paybybank":
            return trans("method.paybybank")
        case "banktransfer":
            return trans("method.banktransfer")
        default:
            return method
    }
}

function sourceLabel(payment: Payment): string | null {
    if (payment.isRefundReversal) {
        return null
    }
    if (payment.mollieRefundId) {
        return "Mollie Refund"
    }
    if (payment.paymentMethod) {
        return translatePaymentMethod(payment.paymentMethod)
    }
    return trans("method.manual")
}
</script>

<template>
    <div class="list-group rounded" v-if="payments.length">
        <div v-for="payment in payments" :key="payment.id" class="list-group-item">
            <div class="d-flex justify-content-between align-items-center">
                <div class="mr-3">
                    <i class="fa fa-fw" :class="iconClasses[paymentKind(payment)]"></i>
                </div>
                <div class="flex-grow-1">
                    <div :class="textClasses[paymentKind(payment)]">
                        {{ labels[paymentKind(payment)] }}
                    </div>
                    <div class="text-muted small">
                        <template v-if="sourceLabel(payment)">{{ sourceLabel(payment) }} &nbsp;·&nbsp;</template>
                        {{ trans("prefix_date") }} {{ $datetime(payment.createdAt) }}
                        <template v-if="viewMode === 'admin' && payment.bookedByName">
                            &nbsp;·&nbsp;
                            {{ trans("booked_by", { name: payment.bookedByName }) }}
                        </template>
                    </div>
                    <!-- Booking notes are office-internal; the backend only serializes them for admins anyway -->
                    <div class="text-muted small font-italic" v-if="viewMode === 'admin' && payment.note">
                        {{ payment.note }}
                    </div>
                </div>

                <div class="badge badge-pill text-white" :class="badgeClasses[paymentKind(payment)]">
                    {{ $currency(payment.amount, 100, { currencyDisplay: "name" }) }}
                </div>
            </div>
        </div>
    </div>
</template>
